
import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow } from
"@/components/ui/table";
import { Search, Plus, Users, Upload, Edit, Trash2, CheckCircle, AlertCircle, KeyRound, RefreshCw, Trash, Download } from "lucide-react";
import AddStudentForm from "../components/admin/AddStudentForm";
import BulkImportStudents from "../components/admin/BulkImportStudents";
import BulkUpdateDrivelineTags from "../components/admin/BulkUpdateDrivelineTags";
import EditStudentForm from "../components/admin/EditStudentForm";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Students() {
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showBulkTags, setShowBulkTags] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [message, setMessage] = useState(null);
  const queryClient = useQueryClient();

  const { data: students = [], isLoading } = useQuery({
    queryKey: ['students'],
    queryFn: () => base44.entities.Student.list()
  });

  const deleteStudentMutation = useMutation({
    mutationFn: async (studentId) => {
      return base44.entities.Student.delete(studentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['students']);
      setMessage({ type: 'success', text: 'Student deleted successfully!' });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err.message || 'Failed to delete student' });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const handleDelete = (student) => {
    if (confirm(`Are you sure you want to delete ${student.first_name} ${student.last_name} (ID: ${student.student_id})?\n\nThis action cannot be undone.`)) {
      deleteStudentMutation.mutate(student.id);
    }
  };

  const resetPasswordMutation = useMutation({
    mutationFn: async (studentId) => {
      const res = await axios.post(
        `${BACKEND_URL}/api/auth/admin/reset-student-password`,
        { student_id: studentId },
        { withCredentials: true }
      );
      return res.data;
    },
    onSuccess: (data) => {
      setMessage({
        type: 'success',
        text: `Password reset for ${data.student_id}. They can now sign in with the default password E@gles26! and will be asked to change it.`
      });
      setTimeout(() => setMessage(null), 6000);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      setMessage({ type: 'error', text: typeof detail === 'string' ? detail : 'Failed to reset password' });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const handleResetPassword = (student) => {
    if (confirm(`Reset password for ${student.first_name} ${student.last_name} (ID: ${student.student_id}) back to the default E@gles26! ?\n\nThe student will be forced to set a new password on their next login.`)) {
      resetPasswordMutation.mutate(student.student_id);
    }
  };

  const wipeAllMutation = useMutation({
    mutationFn: async () => {
      const res = await axios.delete(`${BACKEND_URL}/api/students/all`, {
        withCredentials: true,
      });
      return res.data;
    },
    onSuccess: (data) => {
      // Also nuke any local cached student data from previous mock builds
      try {
        const raw = localStorage.getItem("eagleMockDB_v1");
        if (raw) {
          const db = JSON.parse(raw);
          db.Student = [];
          localStorage.setItem("eagleMockDB_v1", JSON.stringify(db));
        }
      } catch (e) {}
      queryClient.invalidateQueries();
      setMessage({
        type: "success",
        text: `Wiped ${data.deleted} students from the database. You can now re-upload the roster.`,
      });
      setTimeout(() => setMessage(null), 6000);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      setMessage({ type: "error", text: typeof detail === "string" ? detail : "Wipe failed" });
      setTimeout(() => setMessage(null), 5000);
    },
  });

  const downloadBackup = (studentsToBackup) => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const backup = {
      exported_at: new Date().toISOString(),
      count: studentsToBackup.length,
      students: studentsToBackup,
    };
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `eagle-students-backup-${timestamp}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleWipeAll = () => {
    const answer = prompt(
      `⚠️ DANGER: this will DELETE ALL ${students.length} students from the database and log them out.\n\nA JSON backup will be downloaded automatically before wiping.\n\nType DELETE to confirm.`,
    );
    if (answer === "DELETE") {
      if (students.length > 0) downloadBackup(students);
      wipeAllMutation.mutate();
    }
  };

  const normalizeIdsMutation = useMutation({
    mutationFn: async () => {
      const res = await axios.post(`${BACKEND_URL}/api/students/normalize-ids`, null, {
        withCredentials: true,
      });
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["students"]);
      const conflictText = data.conflicts?.length
        ? ` ${data.conflicts.length} conflict(s) skipped (duplicates would be created).`
        : "";
      setMessage({
        type: "success",
        text: `Normalized ${data.updated} student IDs (${data.unchanged} were already correct).${conflictText}`,
      });
      setTimeout(() => setMessage(null), 8000);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      setMessage({ type: "error", text: typeof detail === "string" ? detail : "Normalize failed" });
      setTimeout(() => setMessage(null), 5000);
    },
  });

  const handleNormalizeIds = () => {
    if (confirm("Zero-pad every existing student ID to 6 digits (e.g. 66801 → 066801)?\n\nThis is safe to run multiple times. IDs that already have 6+ digits are left alone.")) {
      normalizeIdsMutation.mutate();
    }
  };

  const forceRefresh = () => {
    // Clear any stale local caches from older builds
    try {
      const raw = localStorage.getItem("eagleMockDB_v1");
      if (raw) {
        const db = JSON.parse(raw);
        db.Student = [];
        localStorage.setItem("eagleMockDB_v1", JSON.stringify(db));
      }
    } catch (e) {}
    queryClient.invalidateQueries(["students"]);
  };

  const filteredStudents = students.filter((student) =>
  student.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  student.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  student.student_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Students</h1>
              <p className="text-slate-600 mt-1">
                Manage Student Database
                <span className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                  {isLoading ? "loading..." : `${students.length} in database`}
                </span>
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={forceRefresh}
              title="Force refresh from database"
              className="text-slate-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={() => downloadBackup(students)}
              disabled={students.length === 0}
              className="text-slate-700"
              title="Download a JSON backup of all students"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button
              onClick={() => setShowBulkImport(true)}
              className="bg-yellow-600 hover:bg-yellow-700">

              <Upload className="w-4 h-4 mr-2" />
              Bulk Import
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowBulkTags(true)}
              className="text-slate-700"
              title="Paste a student_id,driveline_tag CSV to update tags only"
              data-testid="bulk-update-tags-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Bulk Update Tags
            </Button>
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-yellow-600 hover:bg-yellow-700">

              <Plus className="w-4 h-4 mr-2" />
              Add Student
            </Button>
            <Button
              variant="outline"
              onClick={handleNormalizeIds}
              disabled={normalizeIdsMutation.isPending || students.length === 0}
              className="text-slate-700"
              title="Zero-pad every student ID to 6 digits"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              {normalizeIdsMutation.isPending ? "Normalizing..." : "Fix ID Format"}
            </Button>
            <Button
              onClick={handleWipeAll}
              disabled={wipeAllMutation.isPending || students.length === 0}
              variant="outline"
              className="text-red-700 border-red-300 hover:bg-red-50 hover:text-red-800"
              title="Delete every student from the database"
            >
              <Trash className="w-4 h-4 mr-2" />
              {wipeAllMutation.isPending ? "Wiping..." : "Wipe All"}
            </Button>
          </div>
        </div>

        {message && (
          <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            {message.type === 'success' ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-600" />
            )}
            <AlertDescription className={message.type === 'success' ? 'text-green-900' : 'text-red-900'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        <Card className="border-none shadow-lg">
          <CardHeader>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search by name or student ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />

            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student ID</TableHead>
                    <TableHead>First Name</TableHead>
                    <TableHead>Last Name</TableHead>
                    <TableHead>Grade Level</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>F1G1 Parent Phone</TableHead>
                    <TableHead>F1G2 Parent Phone</TableHead>
                    <TableHead>F1G1 Parent Email</TableHead>
                    <TableHead>F2G1 Parent Phone</TableHead>
                    <TableHead>F2G1 Parent Email</TableHead>
                    <TableHead>Driveline Tag</TableHead>
                    <TableHead>Barcode</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ?
                  <TableRow>
                      <TableCell colSpan={13} className="text-center py-8 text-slate-500">
                        Loading...
                      </TableCell>
                    </TableRow> :
                  filteredStudents.length === 0 ?
                  <TableRow>
                      <TableCell colSpan={13} className="text-center py-8 text-slate-500">
                        No students found
                      </TableCell>
                    </TableRow> :

                  filteredStudents.map((student) =>
                  <TableRow key={student.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">{student.student_id}</TableCell>
                        <TableCell>{student.first_name}</TableCell>
                        <TableCell>{student.last_name}</TableCell>
                        <TableCell>{student.grade_level || '-'}</TableCell>
                        <TableCell>{student.email || '-'}</TableCell>
                        <TableCell className="font-mono text-xs">{student.f1g1_parent_phone || '-'}</TableCell>
                        <TableCell className="font-mono text-xs">{student.f1g2_parent_phone || '-'}</TableCell>
                        <TableCell className="text-sm">{student.f1g1_parent_email || '-'}</TableCell>
                        <TableCell className="font-mono text-xs">{student.f2g1_parent_phone || '-'}</TableCell>
                        <TableCell className="text-sm">{student.f2g1_parent_email || '-'}</TableCell>
                        <TableCell className="font-mono text-sm">{student.driveline_tag || '-'}</TableCell>
                        <TableCell className="font-mono text-xs">{student.barcode || '-'}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingStudent(student)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Edit student"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleResetPassword(student)}
                              disabled={resetPasswordMutation.isPending}
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              title="Reset password to default"
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(student)}
                              disabled={deleteStudentMutation.isPending}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete student"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                  )
                  }
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {showAddForm &&
        <AddStudentForm
          onClose={() => setShowAddForm(false)}
          onSuccess={() => {
            setShowAddForm(false);
            queryClient.invalidateQueries(['students']);
            setMessage({ type: 'success', text: 'Student added successfully!' });
            setTimeout(() => setMessage(null), 3000);
          }} />

        }

        {showBulkImport &&
        <BulkImportStudents
          onClose={() => setShowBulkImport(false)}
          onSuccess={() => {
            setShowBulkImport(false);
            queryClient.invalidateQueries(['students']);
          }} />

        }

        {showBulkTags &&
        <BulkUpdateDrivelineTags
          onClose={() => setShowBulkTags(false)}
          onSuccess={() => {
            queryClient.invalidateQueries(['students']);
          }} />
        }

        {editingStudent &&
        <EditStudentForm
          student={editingStudent}
          onClose={() => setEditingStudent(null)}
          onSuccess={() => {
            setEditingStudent(null);
            queryClient.invalidateQueries(['students']);
            setMessage({ type: 'success', text: 'Student updated successfully!' });
            setTimeout(() => setMessage(null), 3000);
          }} />

        }
      </div>
    </div>);

}
